import type { StaffInviteProps } from "./types";

const FONT = "Arial, Helvetica, sans-serif";

function esc(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function sanitize(v: string): string {
  // Strip all HTML tags then escape remaining content
  return esc(v.replace(/<[^>]*>/g, ""));
}

function bullet(text: string): string {
  return `
  <tr>
    <td width="16" valign="top" style="font-family:${FONT};color:#D4A843;font-size:14px;line-height:22px;">&bull;</td>
    <td valign="top" style="font-family:${FONT};color:#374151;font-size:14px;line-height:22px;padding:0 0 6px 4px;">${esc(text)}</td>
  </tr>`;
}

export function generateStaffInviteHTML(p: StaffInviteProps): string {
  const isManager = p.role === "manager";
  const isGroup = p.agencySegment === "grouphome";

  const badgeBg = isManager ? "#1E40AF" : "#374151";
  const badgeText = isManager ? "MANAGER INVITATION" : "TEAM INVITATION";

  const greeting = p.inviteeName
    ? `Hi ${esc(p.inviteeName)},`
    : "You have been invited to join a team.";

  const explainRole = isManager
    ? `${esc(p.inviterName)} has invited you to join ${esc(p.agencyName)} as a Manager on LegacyGrowth Engine.`
    : `${esc(p.inviterName)} has invited you to join ${esc(p.agencyName)} as a Staff Member on LegacyGrowth Engine.`;

  const platformExplain = isGroup
    ? `LegacyGrowth Engine is the platform ${esc(p.agencyName)} uses to manage placement inquiries, referral outreach, and bed occupancy.`
    : `LegacyGrowth Engine is the platform ${esc(p.agencyName)} uses to manage home care leads, referral outreach, and client pipeline.`;

  const cardTitle = `What you will be able to do as a ${isManager ? "Manager" : "Staff Member"}`;

  const managerBullets = [
    `View all leads and inquiries for ${p.agencyName}`,
    "Update lead statuses and add notes",
    "Send personalized referral outreach emails",
    "Manage the referral partner database",
    `Communicate with referral sources on behalf of ${p.agencyName}`,
  ];
  const staffBullets = [
    "View leads and inquiries assigned to you",
    "Update the status of your assigned leads",
    "Add notes to your assigned leads",
    "Communicate updates back to your manager",
  ];
  const bullets = (isManager ? managerBullets : staffBullets).map(bullet).join("");

  const cardNote = isManager
    ? "You will not have access to billing, analytics, or account settings."
    : "You will only see leads that have been assigned to you. Analytics and billing are not visible to staff members.";

  const personalBlock = p.personalMessage && p.personalMessage.trim()
    ? `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#FFFBF0;border-left:4px solid #D4A843;border-radius:0 6px 6px 0;margin-top:20px;">
        <tr><td style="padding:16px 20px;">
          <div style="font-family:${FONT};color:#9A7A1A;font-size:12px;font-weight:bold;line-height:18px;margin-bottom:6px;">A note from ${esc(p.inviterName)}:</div>
          <div style="font-family:${FONT};color:#374151;font-size:14px;line-height:22px;font-style:italic;">${sanitize(p.personalMessage)}</div>
        </td></tr>
      </table>`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>You have been invited to LegacyGrowth Engine</title>
</head>
<body style="margin:0;padding:0;background-color:#F0F2F5;font-family:${FONT};">
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F0F2F5;">
  <tr><td align="center" style="padding:24px 12px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background-color:#FFFFFF;border-radius:8px;">

      <!-- SECTION A: HEADER -->
      <tr><td align="center" style="background-color:#0F1E35;padding:32px 40px;">
        <div style="font-family:${FONT};color:#D4A843;font-size:20px;font-weight:bold;line-height:26px;text-align:center;">LegacyGrowth Engine</div>
        <div style="margin-top:12px;text-align:center;">
          <span style="background-color:${badgeBg};color:#FFFFFF;font-family:${FONT};font-size:11px;font-weight:bold;letter-spacing:1.5px;padding:5px 12px;border-radius:4px;display:inline-block;">${badgeText}</span>
        </div>
        <div style="margin-top:18px;"><div style="border-top:1px solid #D4A843;"></div></div>
      </td></tr>

      <!-- SECTION B: BODY -->
      <tr><td style="background-color:#FFFFFF;padding:40px 40px 28px;">
        <div style="font-family:${FONT};color:#1F3864;font-size:22px;font-weight:bold;line-height:30px;">${greeting}</div>
        <div style="font-family:${FONT};color:#374151;font-size:15px;line-height:24px;margin-top:12px;">${explainRole}</div>
        <div style="font-family:${FONT};color:#6B7280;font-size:14px;line-height:22px;margin-top:8px;">${platformExplain}</div>

        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F8F9FB;border:1px solid #E5E7EB;border-radius:8px;margin-top:20px;">
          <tr><td style="padding:20px;">
            <div style="font-family:${FONT};color:#1F3864;font-size:14px;font-weight:bold;line-height:20px;margin-bottom:12px;">${esc(cardTitle)}</div>
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${bullets}</table>
            <div style="border-top:1px solid #E5E7EB;margin:14px 0 12px;"></div>
            <div style="font-family:${FONT};color:#6B7280;font-size:12px;line-height:18px;">${esc(cardNote)}</div>
          </td></tr>
        </table>

        ${personalBlock}
      </td></tr>

      <!-- SECTION C: CTA -->
      <tr><td style="background-color:#FFFFFF;border-top:1px solid #E5E7EB;padding:28px 40px 36px;" align="center">
        <div style="font-family:${FONT};color:#1F3864;font-size:18px;font-weight:bold;line-height:24px;text-align:center;">Accept your invitation</div>
        <div style="font-family:${FONT};color:#6B7280;font-size:14px;line-height:22px;text-align:center;margin-top:8px;">Click the button below to set your password and access ${esc(p.agencyName)}'s LegacyGrowth Engine account. This invitation expires in ${esc(p.inviteExpiry)}.</div>

        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:20px auto 0;">
          <tr><td align="center" style="background-color:#D4A843;border-radius:6px;">
            <a href="${esc(p.inviteUrl)}" style="display:inline-block;padding:14px 28px;font-family:${FONT};color:#1F3864;font-size:15px;font-weight:bold;text-decoration:none;line-height:20px;">Accept Invitation &amp; Set Password &rarr;</a>
          </td></tr>
        </table>

        <div style="font-family:${FONT};color:#6B7280;font-size:12px;line-height:18px;text-align:center;margin-top:18px;">Button not working? Copy and paste this link into your browser:</div>
        <div style="font-family:${FONT};color:#2E75B6;font-size:12px;line-height:18px;word-break:break-all;text-align:center;margin-top:4px;">${esc(p.inviteUrl)}</div>

        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#FEF2F2;border:1px solid #FECACA;border-radius:6px;margin-top:20px;">
          <tr>
            <td width="24" valign="top" style="padding:12px 0 12px 16px;font-family:${FONT};color:#DC2626;font-size:13px;line-height:18px;">&#9888;</td>
            <td valign="top" style="padding:12px 16px 12px 8px;font-family:${FONT};color:#991B1B;font-size:12px;line-height:18px;">This invitation link expires in ${esc(p.inviteExpiry)}. After that, ${esc(p.inviterName)} will need to send a new invitation.</td>
          </tr>
        </table>
      </td></tr>

      <!-- SECTION D: FOOTER -->
      <tr><td style="background-color:#0F1E35;padding:24px 40px;">
        <div style="font-family:${FONT};line-height:20px;">
          <span style="color:#D4A843;font-size:14px;font-weight:bold;">LegacyGrowth Engine</span><span style="color:#9AAFC8;font-size:14px;"> &mdash; A product of H&amp;E Associates LLC</span>
        </div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:12px;line-height:20px;margin-top:6px;">McLean, Virginia &nbsp;&middot;&nbsp; ${esc(p.supportEmail)}</div>
        <div style="margin-top:10px;font-family:${FONT};font-size:12px;line-height:18px;">
          <a href="mailto:${esc(p.supportEmail)}" style="color:#D4A843;text-decoration:underline;">Contact Support</a>
          <span style="color:#9AAFC8;"> &middot; </span>
          <a href="${esc(p.unsubscribeUrl)}" style="color:#9AAFC8;text-decoration:underline;">Unsubscribe</a>
        </div>
        <div style="font-family:${FONT};color:#4B5563;font-size:11px;line-height:18px;margin-top:12px;">This invitation was sent to ${esc(p.inviteeEmail)} because ${esc(p.inviterName)} at ${esc(p.agencyName)} added this address as a team member on LegacyGrowth Engine. If you did not expect this invitation, you can safely ignore this email. Your information will not be added to any system unless you click the Accept button above.</div>
      </td></tr>

    </table>
  </td></tr>
</table>
</body>
</html>`;
}
